// react components
import React, {
    useEffect,
    useState,
} from 'react'
import {
    IonContent,
    IonPage,
    useIonViewWillEnter,
} from '@ionic/react'
import {
    CancelTokenSource,
} from 'axios'
import {
    useDispatch,
    useSelector,
} from 'react-redux'
import {
    useHistory,
    useLocation,
} from 'react-router-dom'

// components
import {
    IconBlockSite,
    LoaderSite,
    SeoBlockSite,
} from 'components'

// data
import {
    defaultReduxState,
    reduxModalErrorEventHandlerSite,
} from 'data'

// pages
import {
    ContentBlockSite,
    NotFoundPortfolioSite,
    PrivateSideMenuTabWeb,
} from 'pages'

// serializers
import {
    PageDetailSiteSerializer,
    PortfolioPageContentListSiteSerializer,
    PortfolioPrivateSpaceListSiteSerializer,
    errorStatusTypeSite,
} from 'serializers/site'

// services
import {
    getDataSite,
    getPrivateSpaceListDataSite,
    getStylesToRetrieve,
} from 'services'

// props
type PrivateDetailPageSiteProps = {
    detailId: string
    devId: string
    pageSlug: string
}

// main
export const PrivateDetailPageSite: React.FC<PrivateDetailPageSiteProps> = React.memo(({
    detailId,
    devId,
    pageSlug,
}) => {

    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const reduxAuth = useSelector((state: defaultReduxState) => state.reduxAuth)
    const reduxCacheSite = useSelector((state: defaultReduxState) => state.reduxCacheSite)
    const reduxFormSiterefreshPrivateDetailPageSite = useSelector((state: defaultReduxState) => state.reduxFormSite.refresh.PrivateDetailPageSite)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)

    const deviceType = reduxModalSite.deviceType

    const [axiosCancelToken, setAxiosCancelToken] = useState<CancelTokenSource | undefined>(undefined)
    const [errorStatus, setErrorStatus] = useState<errorStatusTypeSite>({})
    const [isLoading, setIsLoading] = useState(false)
    const [pageContentItems, setPageContentItems] = useState<PortfolioPageContentListSiteSerializer[] | undefined>(undefined)
    const [pageObject, setPageObject] = useState<PageDetailSiteSerializer>()
    const [privateSpaceList, setPrivateSpaceList] = useState<PortfolioPrivateSpaceListSiteSerializer[]>([])

    const [fontFamily, setFontFamily] = useState<string>()
    const [linkColor, setLinkColor] = useState<string>()
    const [newPageStyles, setNewPageStyles] = useState<{ [key: string]: string | undefined }>({})
    const [menuOpen, setMenuOpen] = useState(false)
    const [tabObject, setTabObject] = useState<PortfolioPrivateSpaceListSiteSerializer>()
    const [refreshNumber, setRefreshNumber] = useState(reduxFormSiterefreshPrivateDetailPageSite)

    useEffect(() => {
        if (reduxFormSiterefreshPrivateDetailPageSite !== refreshNumber) {
            setRefreshNumber(reduxFormSiterefreshPrivateDetailPageSite)
            getPortfolioData(true)
        } else {
            getPortfolioData()
        }
    }, [
        detailId,
        reduxFormSiterefreshPrivateDetailPageSite,
    ])

    useEffect(() => {
        if (reduxFormSiterefreshPrivateDetailPageSite !== refreshNumber) {
            setRefreshNumber(reduxFormSiterefreshPrivateDetailPageSite)
            getPortfolioData(true)
        } else {
            getPortfolioData()
        }
    }, [
        detailId,
        reduxFormSiterefreshPrivateDetailPageSite,
    ])

    useEffect(() => {
        if (deviceType !== 'is-web') {
            setMenuOpen(false)
        }
    }, [
        location,
    ])

    useIonViewWillEnter(() => {
        scrollToTop()
    })

    useEffect(() => {
        if (reduxCacheSite.portfolio) {
            getPrivateSpaceListDataSite({
                component: 'PrivateDetailPageSite',
                dispatch,
                portfolioObject: reduxCacheSite.portfolio,
                reduxAuth,
                reduxCacheSite,
                reduxModalSite,
                setPrivateSpaceList,
            })
        }
    }, [
        reduxCacheSite.portfolio?.id,
    ])

    useEffect(() => {
        const tempPageStyles: { [key: string]: string | undefined } = {}
        if (getStylesToRetrieve(reduxModalSite, undefined, pageObject?.styles, '', 'backgroundImage')) {
            tempPageStyles.backgroundImage = getStylesToRetrieve(reduxModalSite, undefined, pageObject?.styles, '', 'backgroundImage')
        } else {
            tempPageStyles.background = getStylesToRetrieve(reduxModalSite, undefined, pageObject?.styles, '', 'background')
        }
        tempPageStyles.color = getStylesToRetrieve(reduxModalSite, undefined, pageObject?.styles, '', 'color')
        if (reduxModalSite.navbarType === 'top') {
            tempPageStyles.paddingTop = `${reduxModalSite.navbarIsTransparent ? 0 : reduxModalSite.navbarHeight}px`
        } else if (reduxModalSite.navbarType === 'side') {
            tempPageStyles.paddingLeft = `${reduxModalSite.navbarWidth}px`
        }
        setFontFamily(getStylesToRetrieve(reduxModalSite, undefined, reduxCacheSite.portfolio?.styles, '', 'fontFamily'))
        setNewPageStyles(tempPageStyles)
        setLinkColor(getStylesToRetrieve(reduxModalSite, undefined, pageObject?.styles, '', 'linkColor') || getStylesToRetrieve(reduxModalSite, undefined, reduxCacheSite.portfolio?.styles, '', 'linkColor'))
    }, [
        pageObject?.styles,
        reduxCacheSite.portfolio?.styles,
        reduxModalSite,
    ])

    const scrollToTop = (force?: boolean) => {
        try {
            if (history.action !== 'POP' || force) {
                // @ts-ignore
                if (document.getElementById('mo-content-detail-id-site') && document.getElementById('mo-content-detail-id-site').scrollTo) document.getElementById('mo-content-detail-id-site').scrollTo(0, 0)
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'PrivateDetailPageSite',
                'scrollToTop',
            ))
        }
    }

    function getPortfolioData(forceRefresh?: boolean) {
        try {
            getDataSite({
                axiosCancelToken,
                component: 'PrivateDetailPageSite',
                detailId,
                devId,
                dispatch,
                errorStatus,
                forceRefresh,
                location,
                pageSlug,
                reduxAuth,
                reduxCacheSite,
                reduxModalSite,
                scrollToTop,
                setAxiosCancelToken,
                setErrorStatus,
                setIsLoading,
                setPageContentItems,
                setPageObject,
            })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'PrivateDetailPageSite',
                'getPortfolioData',
            ))
        }
    }

    if (errorStatus.portfolio || errorStatus.page || errorStatus.pageCcontent) {
        return (
            <NotFoundPortfolioSite statusCode={errorStatus.portfolio || errorStatus.page || errorStatus.pageCcontent} />
        )
    }
    return (
        <IonPage>
            <SeoBlockSite data={pageObject?.seo_data} />
            <IonContent className={`mo-hide-ion-content-scrollbar${reduxModalSite.isWindows} ${deviceType}`}>
                {isLoading && (
                    <LoaderSite isOpen />
                )}
                <div
                    className={`private-slug-page-site lg-${reduxModalSite.languageId} ${deviceType}`}
                    style={newPageStyles}
                >
                    <div
                        className={`psps-content ${deviceType}`}
                        style={{
                            fontFamily: fontFamily,
                            height: `calc(100vh - ${reduxModalSite.navbarHeight}px)`,
                        }}
                    >
                        <div
                            className={`psps-left mo-hidden-vertical-scrollbar${reduxModalSite.isWindows}${menuOpen ? ' open' : ''} ${deviceType}`}
                            style={{
                                height: deviceType === 'is-mobile' ? `calc(100vh - ${reduxModalSite.navbarHeight}px - 50px)` : `calc(100vh - ${reduxModalSite.navbarHeight}px)`,
                            }}
                        >
                            {privateSpaceList?.map((val) => (
                                <PrivateSideMenuTabWeb
                                    key={val.id}
                                    object={val}
                                    setTabObject={setTabObject}
                                    tabSlug={pageSlug!}
                                />
                            ))}
                        </div>
                        <div
                            className={`psps-right mo-hidden-vertical-scrollbar${reduxModalSite.isWindows} ${deviceType}`}
                            style={{
                                height: `calc(100vh - ${reduxModalSite.navbarHeight}px)`,
                            }}
                        >
                            <div
                                className={`psps-header${reduxModalSite.isWindows} ${deviceType}`}
                            >
                                <IconBlockSite
                                    className={`hps-icon ${deviceType}`}
                                    color='light'
                                    iconClass='mo-new-icon-list-ul-solid'
                                    onClick={() => setMenuOpen(!menuOpen)}
                                />
                            </div>
                            <div
                                className={`private-slug-page-content-wrap lg-${reduxModalSite.languageId} ${deviceType}`}
                                style={{
                                    background: getStylesToRetrieve(reduxModalSite, undefined, reduxCacheSite.portfolio?.styles, '', 'background'),
                                    color: getStylesToRetrieve(reduxModalSite, undefined, reduxCacheSite.portfolio?.styles, '', 'color'),
                                    fontFamily: fontFamily,
                                    margin: getStylesToRetrieve(reduxModalSite, undefined, reduxCacheSite.portfolio?.styles, '', 'margin'),
                                }}
                            >
                                <div
                                    id='private-slug-page-content-slug'
                                    className={`private-slug-page-content ${isLoading ? 'opacity' : 'no-opacity'}`}
                                >
                                    <div
                                        className={`private-slug-page-content-list ${deviceType}`}
                                        style={{
                                            padding: getStylesToRetrieve(reduxModalSite, undefined, pageObject?.styles, '', 'padding'),
                                        }}
                                    >
                                        {reduxCacheSite.portfolio && pageObject && pageContentItems?.filter(obj => obj.active === true).map((val) => (
                                            <ContentBlockSite
                                                key={val.id}
                                                detailId={detailId}
                                                devId={devId}
                                                isEditHovered={false}
                                                isInDetailPage
                                                linkColor={linkColor}
                                                object={val}
                                                pageSlug={pageSlug}
                                                stylesEdit={undefined}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </IonContent>
        </IonPage>
    )
})
